import { FirebaseError } from "firebase/app";
import { Unsubscribe } from "firebase/auth";
import { makeAutoObservable, runInAction } from "mobx";
import {
  addScenario,
  deleteScenario,
  subscribeScenarios,
  updateScenario,
} from "../api/firebase/collections/scenarioCollection";
import { ContactOption, Scenario } from "../api/firebase/types/scenario.types";
import DeviceStore from "./DeviceStore";
import SessionStore from "./SessionStore";

class ScenarioStore {
  error?: any = null;
  isPending: boolean = false;
  scenarios: Scenario[] = [];
  unsubscribeScenarios?: Unsubscribe;

  constructor() {
    makeAutoObservable(this);
  }

  subscribeScenarios() {
    const onNewSnapshot = (scenarios: Scenario[]) => {
      runInAction(() => {
        this.scenarios = scenarios;
      });
    };
    const onError = (error: FirebaseError) => {
      console.error(error);
      runInAction(() => {
        this.error = error;
      });
    };
    const unsubscribe = subscribeScenarios(onNewSnapshot, onError);
    runInAction(() => {
      this.unsubscribeScenarios = unsubscribe;
    });
  }

  addScenario = async (scenarioToAdd: Omit<Scenario, "id">) => {
    runInAction(() => {
      this.isPending = true;
      this.error = undefined;
    });
    try {
      await addScenario({ ...scenarioToAdd, userId: SessionStore.user!.uid });
      runInAction(() => {
        this.isPending = false;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        this.isPending = false;
        this.error = err;
      });
      return false;
    }
  };

  updateScenario = async (updatedScenario: Scenario) => {
    runInAction(() => {
      this.isPending = true;
      this.error = undefined;
    });
    try {
      await updateScenario(updatedScenario.id, updatedScenario);
      runInAction(() => {
        this.isPending = false;
      });
    } catch (err) {
      console.error(err);
      runInAction(() => {
        this.isPending = false;
        this.error = err;
      });
    }
  };

  deleteScenarios = async (scenarioIds: string[]) => {
    runInAction(() => {
      this.isPending = true;
      this.error = undefined;
    });
    const devicesWithScenarioToDelete =
      DeviceStore.getDevicesByScenarios(scenarioIds);
    if (devicesWithScenarioToDelete.length) {
      throw new Error("in-use");
    }
    const deletionPromises = scenarioIds.map((scenarioId) =>
      deleteScenario(scenarioId)
    );
    const deletionResults = await Promise.allSettled(deletionPromises);
    if (deletionResults.some((result) => result.status === "rejected")) {
      runInAction(() => {
        console.error("some scenarios could not be deleted");
        this.isPending = false;
      });
      return false;
    }
    runInAction(() => {
      this.isPending = false;
    });
    return true;
  };

  deleteContactsFromSettings = (
    idsToDelete: string[],
    contactOptions?: Map<ContactOption, string[]>
  ) => {
    const updatedContactOptions = new Map(contactOptions);
    updatedContactOptions.forEach((contactIds, contactOption) => {
      const updatedIds = contactIds.filter((id) => !idsToDelete.includes(id));
      updatedContactOptions.set(contactOption, updatedIds);
    });
    return updatedContactOptions;
  };

  deleteContactsFromScenarios = (contactIdsToDelete: string[]) => {
    this.scenarios.forEach((scenario) => {
      const updatedContactSettings = this.deleteContactsFromSettings(
        contactIdsToDelete,
        scenario.contactSettings
      );
      this.updateScenario({
        ...scenario,
        contactSettings: updatedContactSettings,
      });
    });
  };

  resetStore = () => {
    this.unsubscribeScenarios && this.unsubscribeScenarios();
    runInAction(() => {
      this.error = null;
      this.isPending = false;
      this.scenarios = [];
      this.unsubscribeScenarios = undefined;
    });
  };
}

export default new ScenarioStore();
